/**
 * components/hire/newJobModal.tsx
 * Create OR edit a Job (requisition) — one premium form, two modes.
 *
 *  - Create: structured fields + paste-a-JD extract (WI-4).
 *  - Edit  : pre-filled from the current revision; on save it asks for
 *            confirmation, PATCHes (append-revision), then shows a success
 *            dialog. Editing never mutates history — it appends a revision.
 *
 * Client-side validation (required Title) blocks the API call and shows a toast;
 * all success/error responses go through the centralized toast handlers.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, Pressable, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { useBreakpoints } from '../../theme/responsive';
import { Modal, ActionButton, Icon } from '../ui';
import CommonInput from '../commonInput';
import { Section, Field, Segmented } from './formKit';
import { web } from '../../utils/web';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { createRequisition, updateRequisition, extractJd, extractJdFile } from '../../services/hire/requisitionV2';
import type { RequisitionDetail, RequisitionInput, RemotePolicy } from '../../schemas/hire/requisitionV2';
import { INPUT_RULES, useInputGuard, type Constraint } from '../../utils/inputConstraints';

// Accepted source documents for the New Job uploader.
const UPLOAD_ACCEPT = '.pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Read a browser File as bare base64 (strips the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const REMOTE: RemotePolicy[] = ['onsite', 'hybrid', 'remote'];
const REMOTE_LABEL: Record<RemotePolicy, string> = { onsite: 'On-site', hybrid: 'Hybrid', remote: 'Remote' };

function toList(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}
function toNum(s: string): number | null {
  const n = Number(s.trim());
  return s.trim() && Number.isFinite(n) ? n : null;
}

export default function NewJobModal({
  open, onClose, onCreated, editing, onUpdated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (r: RequisitionDetail) => void;
  editing?: RequisitionDetail | null;
  onUpdated?: (r: RequisitionDetail) => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { isMobile } = useBreakpoints();
  const isEdit = !!editing;
  // Multi-column field rows collapse to a single column on phones so each field
  // keeps a legible width (and labels/placeholders stop truncating).
  const rowStyle = { flexDirection: isMobile ? ('column' as const) : ('row' as const), gap: isMobile ? 16 : 12 };
  // A cell inside a rowStyle row: equal flex column on desktop, full-width block
  // when the row is stacked (flex:1 in a column would distribute height, not width).
  const cellStyle = isMobile ? ({ width: '100%' } as const) : ({ flex: 1, minWidth: 0 } as const);

  const [title, setTitle] = useState('');
  const [clientAccountId, setClientAccountId] = useState('');
  const [mustHave, setMustHave] = useState('');
  const [niceToHave, setNiceToHave] = useState('');
  const [expMin, setExpMin] = useState('0');
  const [expMax, setExpMax] = useState('');
  const [openings, setOpenings] = useState('1');
  const [remote, setRemote] = useState<RemotePolicy>('onsite');
  const [locations, setLocations] = useState('');
  /**
   * Salary and notice ceilings. Both GATE at Stage 1 — a candidate over either is
   * excluded — and the form had no field for either, so they could only ever be
   * empty: a JD stating "Salary Ceiling: Rs. 5,00,000" and "Notice Period: Maximum
   * 30 days" produced a job reading "Not specified" and "Any", and a candidate
   * asking 8.5L on 90 days' notice matched it as Strong. Left blank they stay
   * unset, which is the old behaviour and still means "no ceiling".
   */
  const [salaryMax, setSalaryMax] = useState('');
  const [noticeMax, setNoticeMax] = useState('');
  /**
   * Education / licence requirement, verbatim ("B.Sc Nursing or GNM", "ITI with a
   * valid wireman licence"). The JD extractor has always returned this and the
   * form had no field for it, so it was extracted and then dropped between the
   * API and the state — exactly what happened to the salary and notice ceilings.
   * It now GATES at Stage 1 via a cached qualification verdict, so a wrong or
   * over-strict value excludes people: left blank it means "no requirement".
   */
  const [education, setEducation] = useState('');
  const [jd, setJd] = useState('');
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jdFocused, setJdFocused] = useState(false); // focus ring for the paste-a-JD box
  // SK-Drive reference for an uploaded source document (JD file), if any.
  const [sourceFileId, setSourceFileId] = useState<string | null>(null);
  const [sourceFilename, setSourceFilename] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false); // highlights the Title box
  const [confirmOpen, setConfirmOpen] = useState(false); // edit → confirm before save
  const [success, setSuccess] = useState<RequisitionDetail | null>(null); // edit → success dialog

  // Strips disallowed characters as the user types and toasts (throttled) when
  // it blocks one. Rules live in utils/inputConstraints.
  const guard = useInputGuard();

  // Prefill from the current revision when opening in edit mode; reset for create.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const r = editing.revision;
      setTitle(r.title ?? '');
      setClientAccountId(editing.clientAccountId ?? '');
      setMustHave((r.mustHaveSkills ?? []).join(', '));
      setNiceToHave((r.niceToHaveSkills ?? []).join(', '));
      setExpMin(String(r.experienceMin ?? 0));
      setExpMax(r.experienceMax == null ? '' : String(r.experienceMax));
      setOpenings(String(r.openingsCount ?? 1));
      setRemote(r.remotePolicy);
      setLocations((r.locations ?? []).join(', '));
      setSalaryMax(r.salaryMax == null ? '' : String(r.salaryMax));
      setNoticeMax(r.noticePeriodMaxDays == null ? '' : String(r.noticePeriodMaxDays));
      setEducation(r.educationMin ?? '');
      setJd(r.descriptionRaw ?? '');
      setSourceFileId(editing.sourceFileId ?? null);
      setSourceFilename(editing.sourceFilename ?? null);
    } else {
      reset();
    }
    setInvalid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const reset = () => {
    setTitle(''); setClientAccountId(''); setMustHave(''); setNiceToHave('');
    setExpMin('0'); setExpMax(''); setOpenings('1'); setRemote('onsite');
    setLocations(''); setJd(''); setInvalid(false);
    setSalaryMax(''); setNoticeMax(''); setEducation('');
    setSourceFileId(null); setSourceFilename(null);
  };

  /**
   * Fill the structured fields from an extraction result (shared by paste + upload).
   *
   * Every gating criterion the extractor can produce must be mapped here. Salary
   * and notice were missing, so a JD that stated both arrived as a job with
   * neither — the extraction was discarded between the API and the form, which is
   * the hardest kind of gap to see: nothing errors, the field is simply empty.
   */
  const applyExtract = (r: {
    title: string; mustHaveSkills: string[]; niceToHaveSkills?: string[];
    experienceMin: number | null; experienceMax: number | null; openings: number | null;
    locations?: string[]; remotePolicy: RemotePolicy | null;
    salaryMax?: number | null; noticePeriodMaxDays?: number | null;
    educationMin?: string | null;
  }) => {
    if (r.title) setTitle(r.title);
    if (r.mustHaveSkills.length) setMustHave(r.mustHaveSkills.join(', '));
    if (r.niceToHaveSkills?.length) setNiceToHave(r.niceToHaveSkills.join(', '));
    if (r.experienceMin != null) setExpMin(String(r.experienceMin));
    if (r.experienceMax != null) setExpMax(String(r.experienceMax));
    if (r.openings != null) setOpenings(String(r.openings));
    if (r.locations?.length) setLocations(r.locations.join(', '));
    if (r.remotePolicy) setRemote(r.remotePolicy);
    if (r.salaryMax != null) setSalaryMax(String(r.salaryMax));
    if (r.noticePeriodMaxDays != null) setNoticeMax(String(r.noticePeriodMaxDays));
    if (r.educationMin) setEducation(r.educationMin);
  };

  // Upload a source document (web file picker) → server parses (PDF→MD / CSV /
  // Excel), extracts fields, and stores the original in SK-Drive.
  const onUpload = () => {
    if (Platform.OS !== 'web') { showToast('error', 'Upload on web', 'File upload is available in the web app.'); return; }
    const doc: any = (globalThis as any).document;
    const input = doc.createElement('input');
    input.type = 'file';
    input.accept = UPLOAD_ACCEPT;
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void onFileSelected(file as File);
    };
    input.click();
  };

  const onFileSelected = async (file: File) => {
    setUploading(true);
    try {
      const contentBase64 = await fileToBase64(file);
      const r = await extractJdFile({ filename: file.name, contentBase64, contentType: file.type || 'application/octet-stream' });
      applyExtract(r);
      setSourceFileId(r.sourceFileId);
      setSourceFilename(r.sourceFilename);
      showToast(
        'success',
        'Read from file',
        r.sourceFileId
          ? 'Extracted the fields — review before saving.'
          : 'Extracted the fields — review before saving. The original file could not be saved.',
      );
    } catch (e) {
      showToast('error', 'Upload failed', getApiErrorMessage(e, 'Could not read that file. Try a PDF, CSV or Excel.'));
    } finally {
      setUploading(false);
    }
  };

  const close = () => { if (!busy) { reset(); onClose(); } };

  const onExtract = async () => {
    if (!jd.trim()) { showToast('error', 'Nothing to extract', 'Paste a job description first.'); return; }
    setExtracting(true);
    try {
      const r = await extractJd(jd);
      applyExtract(r);
      showToast('success', 'Extracted from JD', 'AI read the JD and filled the fields — review before saving.');
    } catch (e) {
      showToast('error', 'Extract failed', getApiErrorMessage(e, 'Could not read the JD. Fill the fields manually.'));
    } finally {
      setExtracting(false);
    }
  };

  const buildInput = (): RequisitionInput => ({
    title: title.trim(),
    clientAccountId: clientAccountId.trim() || null,
    mustHaveSkills: toList(mustHave),
    niceToHaveSkills: toList(niceToHave),
    experienceMin: toNum(expMin) ?? 0,
    experienceMax: toNum(expMax),
    locations: toList(locations),
    remotePolicy: remote,
    salaryCurrency: 'INR',
    // Blank stays null — "no ceiling stated", which is what an empty gate means.
    salaryMax: toNum(salaryMax),
    noticePeriodMaxDays: toNum(noticeMax),
    // Blank stays null — "no requirement stated", which is what an empty gate means.
    educationMin: education.trim() || null,
    openingsCount: toNum(openings) ?? 1,
    descriptionRaw: jd.trim() || null,
    sourceFileId: sourceFileId,
    sourceFilename: sourceFilename,
  });

  /** Shared client-side gate — returns false (and toasts) when invalid. NO API call unless it passes. */
  const validate = (): boolean => {
    if (!title.trim()) {
      setInvalid(true);
      showToast('error', 'Job title is required', 'Enter a title before saving the job.');
      return false;
    }
    setInvalid(false);
    return true;
  };

  // Create path — validate → create → toast.
  const onCreate = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const out = await createRequisition(buildInput());
      const created = out.requisition;
      // The job is created either way; a duplicate verdict just warns that a
      // live job already covers this title for the same client.
      showToast(
        'success',
        out.result === 'duplicate' ? 'Job created — looks like a repeat' : 'Job created',
        out.result === 'duplicate'
          ? `${created.revision?.title ?? title.trim()} is ready, but ${out.duplicateOfCode} already covers this title.`
          : `${created.revision?.title ?? title.trim()} is ready.`,
      );
      reset();
      onCreated(created);
    } catch (e) {
      showToast('error', 'Could not create the job', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // Edit path — validate → confirm dialog → PATCH → success dialog.
  const onSaveClick = () => { if (validate()) setConfirmOpen(true); };

  const onConfirmSave = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      // PATCH appends a revision — header-only fields (clientAccountId, the
      // source-file refs) are NOT revision fields, so the strict patch schema
      // rejects them. Omit them here.
      const { clientAccountId: _omit, sourceFileId: _sfId, sourceFilename: _sfName, ...patch } = buildInput();
      const updated = await updateRequisition(editing.id, patch);
      setConfirmOpen(false);
      setSuccess(updated); // success dialog
    } catch (e) {
      setConfirmOpen(false);
      showToast('error', 'Could not save changes', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSuccessDone = () => {
    const u = success;
    setSuccess(null);
    reset();
    onClose();
    if (u) onUpdated?.(u);
  };

  // One-line text field — shared SK-PLATFORM CommonInput (uppercase label,
  // focus/error states baked in). `numeric` restricts to digits.
  const text = (
    label: string,
    value: string,
    set: (v: string) => void,
    o?: { placeholder?: string; required?: boolean; numeric?: boolean; rule?: Constraint; isValid?: (v: string) => boolean; error?: string; touched?: boolean },
  ) => {
    // Only surface the error message when the value is actually invalid —
    // otherwise CommonInput shows a permanent error border on a filled field.
    const bad = o?.isValid ? !o.isValid(value) : false;
    const rule = o?.numeric ? INPUT_RULES.NUMBER : o?.rule;
    return (
      <CommonInput
        label={label}
        required={o?.required}
        validateOnBlur={false}
        value={value}
        onChangeText={(v) => {
          set(rule ? guard(v, rule) : v);
          if (o?.touched) setInvalid(false);
        }}
        placeholder={o?.placeholder}
        isValid={o?.isValid}
        error={bad ? o?.error : undefined}
        touched={o?.touched}
        mono={o?.numeric}
        inputProps={o?.numeric ? { keyboardType: 'numeric' } : undefined}
        containerStyle={{ marginBottom: 0 }}
      />
    );
  };

  // Multiline variant for skill lists so long comma-separated values wrap and
  // stay fully visible (single-line inputs truncated the AI-extracted skills).
  const area = (label: string, value: string, set: (v: string) => void, placeholder: string, rule?: Constraint) => (
    <CommonInput
      label={label}
      value={value}
      onChangeText={(v) => set(rule ? guard(v, rule) : v)}
      placeholder={placeholder}
      inputProps={{ multiline: true, autoCapitalize: 'none', style: { minHeight: 56, textAlignVertical: 'top', paddingTop: 2 } }}
      containerStyle={{ marginBottom: 0 }}
    />
  );

  // In-modal overlays (confirm → success). Rendered via the Modal's `overlay`
  // prop — NOT a separate RN Modal — so the edit form stays visible behind them
  // (two stacked RN Modals fight on web and the form vanishes).
  const dialogTitle = title.trim() || editing?.title || 'this job';
  const overlayNode =
    confirmOpen ? (
      <OverlayScrim>
        <OverlayCard accent={colors.accent}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accentSoft, alignSelf: 'center' }}>
            <Icon name="save" size={26} color={colors.ink} />
          </View>
          <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center', letterSpacing: -0.2 }}>Save changes?</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, textAlign: 'center', lineHeight: 20 }}>
            This adds a new revision to “{dialogTitle}”. Nothing is overwritten — the previous revision stays in history.
          </Text>
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 6 }}>
            <View style={{ flex: 1 }}><ActionButton label="Keep editing" variant="secondary" onPress={() => setConfirmOpen(false)} /></View>
            <View style={{ flex: 1 }}><ActionButton label="Save changes" variant="primary" loading={busy} onPress={onConfirmSave} /></View>
          </View>
        </OverlayCard>
      </OverlayScrim>
    ) : success ? (
      <OverlayScrim>
        <OverlayCard accent={colors.success}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.successSoft, alignSelf: 'center' }}>
            <Icon name="check-circle" size={28} color={colors.success} />
          </View>
          <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>Job updated</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, textAlign: 'center', lineHeight: 20 }}>
            “{success.revision?.title ?? dialogTitle}” is now at revision {success.currentRevision}. Nothing was overwritten — the earlier revision stays in history.
          </Text>
          <View style={{ marginTop: 4 }}>
            <ActionButton label="Done" variant="primary" onPress={onSuccessDone} />
          </View>
        </OverlayCard>
      </OverlayScrim>
    ) : undefined;

  return (
    <Modal
      open={open}
      onClose={close}
      title={isEdit ? 'Edit job' : 'New job'}
      size="wide"
      overlay={overlayNode}
      footer={
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onPress={close} />
          <ActionButton
            label={isEdit ? 'Save changes' : 'Create job'}
            variant="primary"
            onPress={isEdit ? onSaveClick : onCreate}
            loading={busy}
          />
        </View>
      }
    >
        <View style={{ gap: 20 }}>
          {/* ── Source: paste a JD or upload a document (PDF/CSV/Excel) ── */}
          <Section icon="wand" title="Source" hint="optional — AI fills the fields" first delay={0}>
            <View style={{ gap: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <ActionButton label="Upload file" variant="secondary" size="sm" icon="upload" onPress={onUpload} loading={uploading} />
                <ActionButton label="Extract" variant="secondary" size="sm" icon="wand" onPress={onExtract} loading={extracting} />
              </View>
            </View>
            {sourceFilename && (
              <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start', paddingHorizontal: 10, height: 30, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }]}>
                <Icon name="import" size={13} color={colors.ink3} />
                <Text style={{ fontSize: fontSize.xs, color: colors.ink2, fontWeight: fontWeight.medium }} numberOfLines={1}>{sourceFilename}</Text>
                {sourceFileId ? <Icon name="check-circle" size={13} color={colors.success} /> : <Text style={{ fontSize: 10, color: colors.ink4 }}>not stored</Text>}
                <Pressable onPress={() => { setSourceFileId(null); setSourceFilename(null); }} style={web({ cursor: 'pointer' })}>
                  <Icon name="x" size={13} color={colors.ink4} />
                </Pressable>
              </View>
            )}
            <View style={{ position: 'relative' }}>
              {/* The pasted JD is stored verbatim — NO character filtering. A real
                  job description is full of ₹, %, +, /, •, ()  and digits; the old
                  guard stripped them, which silently corrupted the text the
                  extractor reads. */}
              <TextInput
                value={jd}
                onChangeText={setJd}
                onFocus={() => setJdFocused(true)}
                onBlur={() => setJdFocused(false)}
                placeholder="Paste the job description — any text, symbols or numbers. Extract fills the title + must-have skills."
                placeholderTextColor={colors.ink4}
                multiline
                editable={!extracting}
                style={[
                  { minHeight: 112, padding: 14, paddingBottom: 26, borderWidth: 1.5, borderColor: extracting ? colors.accentLine : jdFocused ? colors.accent : colors.line, borderRadius: radii.lg, backgroundColor: colors.paper2, color: colors.ink, fontSize: fontSize.md, lineHeight: 21, textAlignVertical: 'top' },
                  web({ outline: 'none', transition: 'border-color 160ms ease, box-shadow 160ms ease, background 160ms ease' }),
                  jdFocused && !extracting
                    ? web({ boxShadow: `0 0 0 3px ${colors.accentSoft}, 0 8px 24px -18px rgba(0,0,0,0.55)`, backgroundColor: colors.card } as any)
                    : null,
                ]}
              />
              {/* Live character count — appears only once there's something to
                  count, so the empty box stays clean. */}
              {jd.length > 0 && !extracting && (
                <View
                  style={[
                    { position: 'absolute', right: 10, bottom: 8, paddingHorizontal: 7, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line },
                    web({ animation: 'sk-pop 220ms cubic-bezier(.2,.9,.2,1) both' } as any),
                  ]}
                  pointerEvents="none"
                >
                  <Text style={{ fontSize: 10, color: colors.ink4, fontFamily: theme.font.mono }}>
                    {jd.length.toLocaleString()}
                  </Text>
                </View>
              )}
              {extracting && (
                <View
                  style={[
                    { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: radii.lg, backgroundColor: colors.paper2 },
                    web({ opacity: 0.94, backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any),
                  ]}
                >
                  <ActivityIndicator size="small" color={colors.accent} />
                  <Text
                    style={[
                      { fontSize: fontSize.sm, color: colors.ink3, fontWeight: fontWeight.medium },
                      web({ animation: 'sk-breathe 1400ms ease-in-out infinite' } as any),
                    ]}
                  >
                    Extracting title + skills…
                  </Text>
                </View>
              )}
            </View>
          </View>
          </Section>

          {/* ── Role & skills ── */}
          <Section icon="layers" title="Role & skills" delay={50}>
            {/* Title, must-have and nice-to-have accept ANY character — real job
                titles and skills carry +, #, ., /, (), digits (C++, C#, .NET,
                Node.js, CI/CD, Level-3), so a character filter here silently
                mangled correct input. No `rule` = no stripping. */}
            {text('Title', title, setTitle, {
              placeholder: 'e.g. Senior Backend Engineer (C++ / .NET)',
              required: true,
              isValid: (v) => v.trim().length > 0,
              error: 'Job title is required',
              touched: invalid,
            })}
            <View style={rowStyle}>
              <View style={cellStyle}>{area('Must-have skills', mustHave, setMustHave, 'e.g. node.js, c++, ci/cd, postgres')}</View>
              <View style={cellStyle}>{area('Nice-to-have skills', niceToHave, setNiceToHave, 'e.g. graphql, aws, .net, kafka')}</View>
            </View>
          </Section>

          {/* ── Experience & openings ── */}
          <Section icon="sliders" title="Experience & openings" delay={100}>
            {isMobile ? (
              <View style={{ gap: 16 }}>
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <View style={{ flex: 1, minWidth: 0 }}>{text('Experience min (y)', expMin, setExpMin, { placeholder: 'e.g. 3', numeric: true })}</View>
                  <View style={{ flex: 1, minWidth: 0 }}>{text('Experience max (y)', expMax, setExpMax, { placeholder: 'any', numeric: true })}</View>
                </View>
                <View style={{ width: '100%' }}>{text('Openings', openings, setOpenings, { placeholder: 'e.g. 1', numeric: true })}</View>
              </View>
            ) : (
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <View style={{ flex: 1, minWidth: 0 }}>{text('Experience min (y)', expMin, setExpMin, { placeholder: 'e.g. 3', numeric: true })}</View>
                <View style={{ flex: 1, minWidth: 0 }}>{text('Experience max (y)', expMax, setExpMax, { placeholder: 'any', numeric: true })}</View>
                <View style={{ flex: 1, minWidth: 0 }}>{text('Openings', openings, setOpenings, { placeholder: 'e.g. 1', numeric: true })}</View>
              </View>
            )}
          </Section>

          {/* ── Ceilings ──
              Both gate at Stage 1: a candidate expecting more than the salary
              ceiling, or needing longer than the notice ceiling, is excluded. The
              form previously had neither field, so a JD stating both produced a
              job with neither and the gates never fired. "any" placeholders say
              what blank means — no ceiling, not zero. */}
          <Section icon="sliders" title="Ceilings" delay={150}>
            <View style={rowStyle}>
              <View style={cellStyle}>
                {text('Salary ceiling (₹ / year)', salaryMax, setSalaryMax, { placeholder: 'any', numeric: true })}
              </View>
              <View style={cellStyle}>
                {text('Notice ceiling (days)', noticeMax, setNoticeMax, { placeholder: 'any', numeric: true })}
              </View>
            </View>
          </Section>

          {/* ── Education / licence ──
              A GATING criterion, like the ceilings above: a candidate whose stated
              qualifications do not meet it is excluded at Stage 1, judged by a
              cached qualification verdict rather than string comparison (so "GNM"
              satisfies "B.Sc Nursing or GNM"). Blank means no requirement, and
              "Any" is treated as blank. Deliberately free text — qualifications
              differ by country and by trade, and any dropdown we shipped would
              encode one of them. */}
          <Section icon="shield" title="Education & licence" hint="optional — gates at Stage 1" delay={175}>
            {text('Education / licence requirement', education, setEducation, {
              placeholder: 'e.g. B.Sc Nursing or GNM · ITI with wireman licence · Any graduate',
            })}
          </Section>

          {/* ── Location & work mode ── */}
          <Section icon="map-pin" title="Location & work mode" delay={200}>
            <View style={rowStyle}>
              <View style={cellStyle}>{text('Locations', locations, setLocations, { placeholder: 'e.g. Bengaluru, Remote', rule: INPUT_RULES.LOCATIONS })}</View>
              <View style={cellStyle}>{text('Client account ID', clientAccountId, setClientAccountId, { placeholder: 'Leave blank for in-house', rule: INPUT_RULES.ID_NUMERIC })}</View>
            </View>
            <Field label="Remote policy" upper>
              <Segmented
                value={remote}
                onChange={setRemote}
                options={REMOTE.map((r) => ({ key: r, label: REMOTE_LABEL[r], icon: r === 'onsite' ? 'home' : r === 'hybrid' ? 'layers' : 'globe' }))}
              />
            </Field>
          </Section>
        </View>
      </Modal>
  );
}

/** Full-card scrim for an in-modal dialog (covers the edit form, no second RN Modal). */
function OverlayScrim({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={[
        { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: 'rgba(8,10,14,0.55)' },
        web({ animation: 'sk-rise 160ms ease both', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any),
      ]}
    >
      {children}
    </View>
  );
}

/** The centered dialog card used inside an OverlayScrim. */
function OverlayCard({ accent, children }: { accent: string; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, radii, shadows } = theme;
  return (
    <View
      style={[
        { width: '100%', maxWidth: 400, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' },
        shadows.lg as object,
      ]}
    >
      <View style={{ height: 3, backgroundColor: accent }} />
      <View style={{ padding: 20, gap: 12 }}>{children}</View>
    </View>
  );
}
